// frontend/admin-portal/src/components/sales/InvoiceModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Input,
  Alert,
  formatMoney,
  InvoiceTemplate,
} from "@fiberhome/shared-ui";
import { invoiceService } from "../../services/invoiceService";
import { resolveImageUrl } from "../../services/fileService";
import { useStoreIdentity } from "../../hooks/useStoreIdentity";
import CustomerPicker from "../orders/CustomerPicker";
import CustomerModal from "../orders/CustomerModal";
import InvoiceItemsEditor from "./InvoiceItemsEditor";

const EMPTY = {
  code: "",
  customerId: "",
  issueDate: new Date().toISOString().slice(0, 10),
  dueDate: "",
  currency: "USD",
  discount: 0,
  shipping: 0,
  notes: "",
  paymentTerms: "",
  items: [],
};

export default function InvoiceModal({ open, onClose, onSaved }) {
  const store = useStoreIdentity();

  const [form, setForm] = useState(EMPTY);
  const [customer, setCustomer] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("draft"); // draft | issue
  const [tab, setTab] = useState("editor"); // editor | preview

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setCustomer(null);
    setError("");
    setMode("draft");
    setTab("editor");
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ── Derived totals ────────────────────────────────────────────
  const totals = useMemo(() => {
    let subtotal = 0;
    let taxTotal = 0;
    for (const it of form.items) {
      const gross =
        (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0) -
        (Number(it.discount) || 0);
      const tax = (gross * (Number(it.taxRate) || 0)) / 100;
      subtotal += gross;
      taxTotal += tax;
    }
    const total =
      subtotal +
      taxTotal +
      (Number(form.shipping) || 0) -
      (Number(form.discount) || 0);
    return {
      subtotal: +subtotal.toFixed(2),
      taxTotal: +taxTotal.toFixed(2),
      total: +total.toFixed(2),
    };
  }, [form.items, form.discount, form.shipping]);

  // ── Preview object (InvoiceTemplate expects this shape) ──────
  const previewInvoice = useMemo(() => {
    return {
      code: form.code || "DRAFT",
      kind: "invoice",
      status: mode === "issue" ? "issued" : "draft",
      issuedAt: form.issueDate,
      issueDate: form.issueDate,
      validUntil: form.dueDate,
      dueDate: form.dueDate,
      currency: form.currency,
      customerName: customer?.name || "— pick a customer —",
      customerPhone: customer?.phone || "",
      customerEmail: customer?.email || "",
      customerAddress:
        [customer?.address, customer?.city, customer?.country]
          .filter(Boolean)
          .join(", ") || "",
      subtotal: totals.subtotal,
      discountAmount: Number(form.discount) || 0,
      taxAmount: totals.taxTotal,
      shipping: Number(form.shipping) || 0,
      totalAmount: totals.total,
      notes: form.notes,
      termsAndConditions: form.paymentTerms,
      items: form.items.map((it, i) => ({
        id: i,
        productName: it.description || "—",
        productSku: it.productSku || "",
        quantity: Number(it.quantity) || 0,
        unit: it.unit,
        unitPrice: Number(it.unitPrice) || 0,
        discountPercent: 0,
        lineTotal:
          ((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0) -
            (Number(it.discount) || 0)) *
          (1 + (Number(it.taxRate) || 0) / 100),
      })),
    };
  }, [form, customer, totals, mode]);

  // ── Submit ────────────────────────────────────────────────────
  const submit = async (submitMode) => {
    if (!customer) {
      setError("Please select a customer.");
      return;
    }
    if (form.items.length === 0) {
      setError("Add at least one line item.");
      return;
    }
    setSaving(true);
    setError("");
    setMode(submitMode);
    try {
      const payload = {
        customerId: customer.id,
        issueDate: form.issueDate,
        dueDate: form.dueDate || null,
        discount: Number(form.discount) || 0,
        shipping: Number(form.shipping) || 0,
        notes: form.notes,
        paymentTerms: form.paymentTerms,
        status: submitMode === "issue" ? "issued" : "draft",
        items: form.items.map((it) => ({
          productId: it.productId || null,
          description: it.description,
          quantity: Number(it.quantity) || 0,
          unitPrice: Number(it.unitPrice) || 0,
          discount: Number(it.discount) || 0,
          taxRate: Number(it.taxRate) || 0,
          unit: it.unit || "pcs",
          unitCostSnapshot: it.unitCostSnapshot || 0,
        })),
      };
      await invoiceService.create(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const logoUrl = store.logo ? resolveImageUrl(store.logo) : "";
  const itemsCount = form.items.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Invoice"
      size="xl"
      bodyClassName="fh-invoice-modal-body"
      footer={
        <div className="fh-invoice-modal-footer">
          <div className="fh-invoice-modal-footer__totals">
            <div>
              <span>Subtotal</span>
              <strong>
                {formatMoney(totals.subtotal, { symbol: form.currency })}
              </strong>
            </div>
            <div>
              <span>Tax</span>
              <strong>
                {formatMoney(totals.taxTotal, { symbol: form.currency })}
              </strong>
            </div>
            <div className="fh-invoice-modal-footer__total">
              <span>Total</span>
              <strong>
                {formatMoney(totals.total, { symbol: form.currency })}
              </strong>
            </div>
          </div>
          <div className="fh-invoice-modal-footer__actions">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => submit("draft")}
              disabled={saving}
            >
              Save Draft
            </Button>
            <Button
              variant="primary"
              onClick={() => submit("issue")}
              loading={saving && mode === "issue"}
              disabled={saving}
            >
              Save & Issue
            </Button>
          </div>
        </div>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* ── Tabs ──────────────────────────────────────────────── */}
      <div className="fh-invoice-modal__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "editor"}
          className={`fh-invoice-modal__tab ${
            tab === "editor" ? "is-active" : ""
          }`}
          onClick={() => setTab("editor")}
        >
          ✏️ Editor
          {itemsCount > 0 && (
            <span className="fh-invoice-modal__tab-badge">{itemsCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "preview"}
          className={`fh-invoice-modal__tab ${
            tab === "preview" ? "is-active" : ""
          }`}
          onClick={() => setTab("preview")}
        >
          👁 Preview
        </button>
      </div>

      {/* ── Editor tab ────────────────────────────────────────── */}
      {tab === "editor" && (
        <div className="fh-invoice-modal__pane">
          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__label">Customer *</div>
            <div className="d-flex gap-2 align-items-start">
              <div className="flex-grow-1">
                <CustomerPicker
                  value={customer}
                  onChange={(c) => {
                    setCustomer(c);
                    set("customerId", c?.id || "");
                  }}
                  onCreateNew={() => setCustomerModalOpen(true)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCustomerModalOpen(true)}
              >
                + New
              </Button>
            </div>
          </div>

          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__grid">
              <div>
                <Input
                  label="Issue Date"
                  type="date"
                  value={form.issueDate}
                  onChange={(e) => set("issueDate", e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Due Date"
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => set("dueDate", e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => set("currency", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__label">Line Items</div>
            <InvoiceItemsEditor
              items={form.items}
              onChange={(items) => set("items", items)}
              currency={form.currency}
            />
          </div>

          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__grid">
              <div>
                <Input
                  label="Discount (invoice-level)"
                  type="number"
                  step="0.01"
                  value={form.discount}
                  onChange={(e) => set("discount", e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Shipping"
                  type="number"
                  step="0.01"
                  value={form.shipping}
                  onChange={(e) => set("shipping", e.target.value)}
                />
              </div>
              <div />
            </div>
          </div>

          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__label">Notes</div>
            <textarea
              className="fh-input"
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Visible on the printed invoice"
            />
          </div>

          <div className="fh-invoice-modal__section">
            <div className="fh-invoice-modal__label">Payment Terms</div>
            <textarea
              className="fh-input"
              rows={2}
              value={form.paymentTerms}
              onChange={(e) => set("paymentTerms", e.target.value)}
              placeholder="e.g. Net 30 from invoice date"
            />
          </div>
        </div>
      )}

      {/* ── Preview tab ───────────────────────────────────────── */}
      {tab === "preview" && (
        <div className="fh-invoice-modal__pane fh-invoice-modal__pane--preview">
          <div className="fh-invoice-modal__preview-hint">
            This is exactly what will be printed / saved as PDF. Switch to
            Editor to make changes.
          </div>
          <InvoiceTemplate
            kind="invoice"
            order={previewInvoice}
            store={store}
            lang="en"
            logoUrl={logoUrl}
          />
        </div>
      )}

      <CustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSaved={(c) => {
          setCustomer(c);
          set("customerId", c?.id || "");
          setCustomerModalOpen(false);
        }}
      />
    </Modal>
  );
}
